import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { TextInput, TextArea, MultiSelectChips } from '../FormElements';
import { StepIndicator } from './StepIndicator';
import { FileUpload } from './FileUpload';
import { RangeSlider } from './RangeSlider';
const WEB3FORMS_KEY = '9d55d263-83bb-4e6f-b05a-3b8d38836fdc';
interface BrandOnboardingProps {
  onComplete: (data: any) => void;
}
const STEPS = [{
  name: 'Your Story'
}, {
  name: 'Identity'
}, {
  name: 'Audience'
}, {
  name: 'Goals'
}, {
  name: 'Investment'
}, {
  name: 'Dream Partner'
}];
export function BrandOnboarding({
  onComplete
}: BrandOnboardingProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    description: '',
    origin: '',
    website: '',
    instagram: '',
    linkedin: '',
    ageMin: 18,
    ageMax: 65,
    interests: [] as string[],
    idealCustomer: '',
    primaryGoal: '',
    successDefinition: '',
    budgetMin: 5000,
    budgetMax: 50000,
    timing: '',
    activations: [] as string[],
    eventTypes: [] as string[],
    values: [] as string[],
    dreamMatch: ''
  });
  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const submitToEmail = async () => {
    setIsSubmitting(true);
    // Get the access code used to login
    const accessCode = localStorage.getItem('sponsrai_access') || 'Unknown';
    try {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `🏢 Brand Profile Complete: ${formData.name}`,
          from_name: 'SponsrAI Dashboard',
          to: 'info@sponsrai.se',
          'Profile Type': 'BRAND PROFILE (Full)',
          'Access Code Used': accessCode,
          '--- STORY ---': '',
          'Brand Name': formData.name,
          Tagline: formData.tagline,
          'What They Do': formData.description,
          'Origin Story': formData.origin,
          '--- IDENTITY ---': '',
          Website: formData.website || 'Not provided',
          Instagram: formData.instagram || 'Not provided',
          LinkedIn: formData.linkedin || 'Not provided',
          '--- AUDIENCE ---': '',
          'Target Age Range': `${formData.ageMin} - ${formData.ageMax}`,
          'Target Interests': formData.interests.join(', ') || 'Not specified',
          'Ideal Customer': formData.idealCustomer || 'Not provided',
          '--- GOALS ---': '',
          'Primary Goal': formData.primaryGoal || 'Not specified',
          'Success Definition': formData.successDefinition || 'Not provided',
          '--- INVESTMENT ---': '',
          'Budget Range': `Up to €${formData.budgetMax.toLocaleString()}`,
          Timing: formData.timing || 'Not specified',
          'Activation Types': formData.activations.join(', ') || 'Not specified',
          '--- DREAM PARTNER ---': '',
          'Preferred Event Types': formData.eventTypes.join(', ') || 'Not specified',
          'Values Alignment': formData.values.join(', ') || 'Not specified',
          'Dream Match Description': formData.dreamMatch || 'Not provided'
        })
      });
    } catch (err) {
      console.error('Failed to submit profile:', err);
    }
    setIsSubmitting(false);
    onComplete(formData);
  };
  const handleNext = () => {
    if (step < STEPS.length) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    } else {
      submitToEmail();
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };
  return <div className="max-w-3xl mx-auto" data-id="element-55">
      <StepIndicator steps={STEPS} currentStep={step} accentColor="cyan" data-id="element-56" />

      <motion.div key={step} initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 mb-8" data-id="element-57">
        {step === 1 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-58">
              Let's start with who you are
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-59">
              Tell us the story behind the brand.
            </p>

            <TextInput label="Brand / Company Name" placeholder="e.g. Acme Innovations" value={formData.name} onChange={e => handleChange('name', e.target.value)} data-id="element-60" />
            <TextInput label="Tagline" placeholder="Sum up your brand in one catchy line" value={formData.tagline} onChange={e => handleChange('tagline', e.target.value)} data-id="element-61" />
            <TextArea label="What do you do?" placeholder="Describe your product or service in 2-3 sentences..." value={formData.description} onChange={e => handleChange('description', e.target.value)} data-id="element-62" />
            <TextArea label="Why did you start?" placeholder="What's your origin story? What drives you?" value={formData.origin} onChange={e => handleChange('origin', e.target.value)} data-id="element-63" />
          </>}

        {step === 2 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-64">
              Show us your brand
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-65">
              Visuals help organizers understand your vibe.
            </p>

            <FileUpload label="Upload Logo" accentColor="cyan" data-id="element-66" />

            <TextInput label="Website URL" placeholder="https://" value={formData.website} onChange={e => handleChange('website', e.target.value)} data-id="element-67" />
            <div className="grid grid-cols-2 gap-4" data-id="element-68">
              <TextInput label="Instagram Handle (Optional)" placeholder="@yourbrand" value={formData.instagram} onChange={e => handleChange('instagram', e.target.value)} data-id="element-69" />
              <TextInput label="LinkedIn (Optional)" placeholder="linkedin.com/company/..." value={formData.linkedin} onChange={e => handleChange('linkedin', e.target.value)} data-id="element-70" />
            </div>
          </>}

        {step === 3 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-71">
              Who do you reach?
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-72">
              Help us find events with a matching audience.
            </p>

            <div className="mb-8" data-id="element-73">
              <RangeSlider label="Target Age Range (Min)" min={13} max={80} defaultValue={formData.ageMin} onChange={v => handleChange('ageMin', v)} accentColor="cyan" data-id="element-74" />
              <RangeSlider label="Target Age Range (Max)" min={13} max={80} defaultValue={formData.ageMax} onChange={v => handleChange('ageMax', v)} accentColor="cyan" data-id="element-75" />
            </div>

            <MultiSelectChips label="Target Interests" options={['Sports', 'Music', 'Tech', 'Gaming', 'Lifestyle', 'Business', 'Art', 'Food', 'Fitness', 'Travel', 'Fashion', 'Sustainability']} selectedValues={formData.interests} onChange={vals => handleChange('interests', vals)} accentColor="cyan" data-id="element-76" />

            <TextInput label="Describe your ideal customer" placeholder="e.g. Urban professionals who love outdoor adventures..." value={formData.idealCustomer} onChange={e => handleChange('idealCustomer', e.target.value)} data-id="element-77" />
          </>}

        {step === 4 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-78">
              What do you want?
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-79">
              Be clear about your sponsorship goals.
            </p>

            <div className="mb-6" data-id="element-80">
              <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-81">
                Primary Goal
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-82">
                {['Brand Awareness', 'Lead Generation', 'Product Launch', 'Community Building', 'Talent Recruitment', 'Market Expansion'].map(goal => <button key={goal} onClick={() => handleChange('primaryGoal', goal)} className={`p-4 rounded-xl border text-left transition-all ${formData.primaryGoal === goal ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500'}`} data-id="element-83">
                    {goal}
                  </button>)}
              </div>
            </div>

            <TextArea label="What does success look like?" placeholder="e.g. We want to get our product in 5000 hands and generate 500 qualified leads..." value={formData.successDefinition} onChange={e => handleChange('successDefinition', e.target.value)} data-id="element-84" />
          </>}

        {step === 5 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-85">
              Let's talk budget
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-86">
              This helps us match you with realistic opportunities.
            </p>

            <RangeSlider label="Budget Range (Max)" min={1000} max={100000} step={1000} defaultValue={formData.budgetMax} formatValue={v => `€${v.toLocaleString()}`} onChange={v => handleChange('budgetMax', v)} accentColor="cyan" data-id="element-87" />

            <div className="mb-6" data-id="element-88">
              <label className="block text-sm font-medium text-slate-300 mb-3" data-id="element-89">
                Timing
              </label>
              <select className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500" value={formData.timing} onChange={e => handleChange('timing', e.target.value)} data-id="element-90">
                <option value="" data-id="element-91">Select timing...</option>
                <option value="Ready now" data-id="element-92">Ready now</option>
                <option value="Next 3 months" data-id="element-93">Next 3 months</option>
                <option value="Next 6 months" data-id="element-94">Next 6 months</option>
                <option value="Planning ahead" data-id="element-95">Planning ahead</option>
              </select>
            </div>

            <MultiSelectChips label="Preferred Activation Types" options={['Product Sampling', 'Speaking', 'Exhibition', 'Digital Campaign', 'Experiential', 'Content Creation']} selectedValues={formData.activations} onChange={vals => handleChange('activations', vals)} accentColor="cyan" data-id="element-96" />
          </>}

        {step === 6 && <>
            <h2 className="text-3xl font-bold text-white mb-2" data-id="element-97">
              Dream Partner
            </h2>
            <p className="text-slate-400 mb-8" data-id="element-98">Describe your perfect match.</p>

            <MultiSelectChips label="Preferred Event Types" options={['Festivals', 'Conferences', 'Sports', 'Community', 'Charity', 'Corporate', 'Digital']} selectedValues={formData.eventTypes} onChange={vals => handleChange('eventTypes', vals)} accentColor="cyan" data-id="element-99" />

            <MultiSelectChips label="Values Alignment" options={['Sustainability', 'Innovation', 'Diversity', 'Health', 'Education', 'Entertainment', 'Social Impact']} selectedValues={formData.values} onChange={vals => handleChange('values', vals)} accentColor="cyan" data-id="element-100" />

            <TextArea label="What would make you say 'this is the one'?" placeholder="e.g. An event that truly cares about sustainability and has a highly engaged, tech-savvy audience..." value={formData.dreamMatch} onChange={e => handleChange('dreamMatch', e.target.value)} data-id="element-101" />
          </>}
      </motion.div>

      <div className="flex justify-between" data-id="element-102">
        <button onClick={handleBack} disabled={step === 1 || isSubmitting} className={`px-6 py-3 rounded-full font-medium transition-colors flex items-center gap-2 ${step === 1 || isSubmitting ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`} data-id="element-103">
          <ArrowLeft size={18} data-id="element-104" /> Back
        </button>
        <button onClick={handleNext} disabled={isSubmitting} className="px-8 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 rounded-full font-bold transition-all shadow-[0_0_20px_rgba(6,182,212,0.3)] flex items-center gap-2 disabled:opacity-50" data-id="element-105">
          {isSubmitting ? <>
              <Loader2 size={18} className="animate-spin" data-id="element-106" /> Saving...
            </> : <>
              {step === STEPS.length ? 'Finish Profile' : 'Next Step'}{' '}
              <ArrowRight size={18} data-id="element-107" />
            </>}
        </button>
      </div>
    </div>;
}